import { cn } from "@/lib/utils";

/**
 * Skeleton — the one UI primitive the `@tb` registry does not offer
 * (docs/internal/ds-variant-candidates.md).
 *
 * Each line or card animates on its own, with a staggered delay: that is what makes the wave
 * run top to bottom instead of one block blinking whole. Reduced motion is neutralised by the
 * `.tb-panel-root` rule in `globals.css`.
 */

/** Handoff §5: 0.09s of delay per table row. */
export const SKELETON_ROW_DELAY_SECONDS = 0.09;

/** Handoff §5: 0.12s of delay per card. */
export const SKELETON_CARD_DELAY_SECONDS = 0.12;

type SkeletonRegionProps = {
  /** Names what is loading, for whoever is on a screen reader. */
  label: string;
  children?: React.ReactNode;
  className?: string;
};

export function SkeletonRegion({ label, children, className }: SkeletonRegionProps) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className={className}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

type SkeletonPulseProps = {
  /** Position in the sequence; sets the animation delay. */
  delayIndex?: number;
  /** Delay step in seconds. A row by default; a card takes the larger step. */
  delayStep?: number;
  children?: React.ReactNode;
  className?: string;
};

export function SkeletonPulse({
  delayIndex = 0,
  delayStep = SKELETON_ROW_DELAY_SECONDS,
  children,
  className,
}: SkeletonPulseProps) {
  // `toFixed` keeps 0.27000000000000002 out of the style attribute.
  const delaySeconds = Number((delayIndex * delayStep).toFixed(2));

  return (
    <div
      aria-hidden="true"
      style={{ animationDelay: `${delaySeconds}s` }}
      className={cn("animate-[tb-skel-pulse_1.4s_var(--tb-ease-linen)_infinite]", className)}
    >
      {children}
    </div>
  );
}

type SkeletonBarProps = {
  height: number | string;
  width: number | string;
  className?: string;
};

function toCssSize(value: number | string): string {
  return typeof value === "number" ? `${value}px` : value;
}

export function SkeletonBar({ height, width, className }: SkeletonBarProps) {
  return (
    <span
      className={cn("block bg-tb-porcelain", className)}
      style={{ height: toCssSize(height), width: toCssSize(width) }}
    />
  );
}
